import { trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  input,
  signal,
} from '@angular/core';
import { AssetDto, GalleryDto, GalleryFeedbackOptions } from '@nusign/picflow-backend-types';
import { maxHeightIf } from '../../../animations/max-height.if.animation';
import { billingScopes } from '../../../billing-consts';
import { GROWTHBOOK } from '../../../growthbook';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { fixContentEditableStyles } from '../../../lib/fix-contenteditable-styles';
import { isMetaKey } from '../../../lib/is-meta-key';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { UserLike } from '../../../services/avatar.service';
import { CheckoutService } from '../../../services/checkout.service';
import { CommentService } from '../../../services/comment.service';
import { PermissionService } from '../../../services/permission.service';
import { ClickOutsideDirective } from '../../../shared-extra/directives/click-outside.directive';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { CommentItemComponent } from '../comment-item/comment-item.component';
import { Comment, CommentThread } from '../comments/comments.component';
import { EmojiSelectorComponent } from '../lazy/emoji-selector/emoji-selector.component';

@Component({
  selector: 'app-comment-thread',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './comment-thread.component.html',
  styleUrl: './comment-thread.component.scss',
  animations: [
    trigger('replyActions', maxHeightIf(40)),
    trigger('threadReply', maxHeightIf(100)),
    trigger('replyBox', maxHeightIf(56)),
  ],
  imports: [
    AvatarComponent,
    SvgComponent,
    ContextMenuComponent,
    ButtonDirective,
    CommentItemComponent,
    WysiwygDirective,
    EmojiSelectorComponent,
    ClickOutsideDirective,
  ],
})
export class CommentThreadComponent {
  public growthBook = inject(GROWTHBOOK);
  private check = inject(ChangeDetectorRef);
  private commentService = inject(CommentService);
  private permissionService = inject(PermissionService);
  private checkoutService = inject(CheckoutService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private assetFeedbackService = inject(AssetFeedbackService);

  @Input() thread: CommentThread;
  @Input() accessLevel: GalleryAccessLevel;
  @Input() enabledAndAllowedFeedbackOptions: GalleryFeedbackOptions[];
  @Input() isTeamMember: boolean;
  @Input() me: UserLike;
  @Input() selected: boolean;
  @Input() hover: boolean;
  @Input() gallery: GalleryDto;
  @Input() asset: AssetDto;
  @Input() expandReplies: boolean;
  replies = input.required<Comment[]>();

  privateReply = signal(false);

  @Output() deleteComment = new EventEmitter<Comment | CommentThread>();
  @Output() resolveThread = new EventEmitter<boolean>();
  @Output() threadHover = new EventEmitter<CommentThread>();
  @Output() unselect = new EventEmitter<void>();
  @Output() replyFocusChanged = new EventEmitter<boolean>();
  @Output() selectThread = new EventEmitter<void>();
  @Output() toggleSelectedThread = new EventEmitter<void>();

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (!this.messageFocused()) return;
    const metaKey = isMetaKey(event);
    if (event.target['getAttribute']?.('name') === 'reply') {
      if (event.key === 'Enter' && metaKey) this.sendReply();
    }
  }

  replyText = '';
  messageFocused = signal(false);
  emojiMenuIsOpen = signal(false);

  focusMessage() {
    this.messageFocused.set(true);
    this.replyFocusChanged.emit(true);
  }

  unfocusMessage() {
    this.messageFocused.set(false);
    this.replyFocusChanged.emit(false);
  }

  togglePrivateClick(event: Event) {
    event.stopPropagation();
    if (this.permissionService.canDisplayElement(billingScopes.asset.privateComments)) {
      this.privateReply.set(!this.privateReply());
    } else {
      this.checkoutService.open(billingScopes.asset.privateComments);
    }
  }

  addEmoji(emoji: { emoji: { native: string }; $event: PointerEvent }) {
    this.replyText += emoji.emoji.native;
    this.check.detectChanges();
    emoji.$event.stopPropagation();
  }

  async sendReply() {
    const text = fixContentEditableStyles(this.replyText.replace(/<br>/g, '\n').trim().replace(/\n/g, '<br>'));
    this.analytics.track('comment_added', false, {
      annotation: false,
      text: true,
      reply: true,
      private: this.privateReply(),
    });
    await this.assetFeedbackService.sendComment(
      this.asset,
      this.gallery,
      { text, parent_comment: this.thread.id, private: this.thread.private || this.privateReply() },
      []
    );
    this.replyText = '';
    this.check.detectChanges();
    this.unfocusMessage();
  }

  updateComment(comment: Comment, text: string) {
    text = fixContentEditableStyles(text.replace(/<br>/g, '\n').trim().replace(/\n/g, '<br>'));
    this.analytics.track('comment_updated', false, {
      annotation: !!comment.annotation,
      text: !!comment.text,
      reply: !!comment.parent_comment,
    });
    this.commentService.updateComment(this.thread.asset, comment.id, { text }, this.gallery.id).subscribe();
  }

  messageClick(event: Event) {
    if (!this.selected) return;
    event.stopPropagation();
  }
}
